import {ReactNode} from "react";

export type AppBootstrapGateViewState = "loading" | "pending" | "error";

export type AppBootstrapGateViewProps = {
  state: AppBootstrapGateViewState;
  brandName: string;
  brandLogo?: ReactNode;
  onRetryClick?: () => void;
};

export const AppBootstrapGateView = (props: AppBootstrapGateViewProps) => {
  const {state, brandName, brandLogo, onRetryClick} = props;

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-700 dark:bg-[#081222] dark:text-zinc-300">
        <div className="text-center">
          <div
            className="mb-4 h-10 w-10 rounded-full border-2 border-[#12284C] border-t-transparent animate-spin mx-auto dark:border-zinc-200 dark:border-t-transparent"/>
          <p className="text-sm text-slate-500 dark:text-zinc-400">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  const pending = state === "pending";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 dark:bg-[#081222]">
      <div className="relative w-full max-w-md">
        <div className="pointer-events-none">
          <div className="absolute -top-16 -right-10 h-32 w-32 rounded-full bg-[#12284C]/10 blur-2xl dark:bg-white/5"/>
          <div className="absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-[#E1251B]/10 blur-3xl dark:bg-[#E1251B]/15"/>
        </div>

        <div
          className="relative rounded-3xl border border-slate-200 bg-white shadow-[0_22px_50px_rgba(15,23,42,0.12)] px-8 py-8 dark:border-white/10 dark:bg-[#0B182E] dark:shadow-black/30 sm:px-10 sm:py-10">
          <div className="mb-6 flex items-center justify-center gap-3">
            {brandLogo && (
              <div
                className="h-9 w-9 rounded-xl bg-[#12284C] flex items-center justify-center shadow-md shadow-slate-300 dark:bg-white dark:shadow-black/20">
                <div
                  className="h-5 w-5 rounded-md bg-white flex items-center justify-center text-[0.6rem] font-semibold tracking-tight text-[#12284C] dark:bg-zinc-950 dark:text-white">
                  {brandLogo}
                </div>
              </div>
            )}
            <div className="text-left">
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-zinc-400">
                {brandName}
              </p>
              <p className="text-xs text-slate-500 dark:text-zinc-500">
                {pending ? "Pending workspace access" : "Workspace bootstrap"}
              </p>
            </div>
          </div>

          <div className="mb-4 flex justify-center">
            <div
              className="h-12 w-12 rounded-2xl bg-[#E1251B]/5 text-[#E1251B] flex items-center justify-center text-xl font-semibold dark:bg-[#E1251B]/10">
              !
            </div>
          </div>

          <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900 text-center mb-2 dark:text-white">
            {pending ? "Your account is not active here yet" : "Something went wrong"}
          </h1>

          {pending ? (
            <>
              <p className="text-sm text-slate-600 text-center mb-4 dark:text-zinc-300">
                We found a valid sign-in, but there is no {brandName} profile for you
                in this tenant. This usually means:
              </p>

              <ul className="text-xs text-slate-600 mb-6 space-y-2 list-disc list-inside dark:text-zinc-400">
                <li>Your invite has not been accepted yet.</li>
                <li>Your access request is still pending approval.</li>
                <li>You are trying to sign into the wrong tenant or subdomain.</li>
              </ul>

              <p className="text-xs text-slate-500 text-center mb-6 dark:text-zinc-500">
                If you were expecting access, contact your {brandName} administrator
                and share the email you used to sign in.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 text-center mb-6 dark:text-zinc-300">
                We started authentication, but something went wrong loading your {brandName} profile. Please try again.
              </p>

              <p className="text-xs text-slate-500 text-center mb-6 dark:text-zinc-500">
                This usually happens if your session changed or your tenant /
                permissions could not be resolved. A quick retry will restart the
                sign-in and bootstrap process.
              </p>
            </>
          )}

          {onRetryClick && (
            <div className="flex justify-center">
              <button
                onClick={onRetryClick}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#12284C] text-white font-semibold text-sm hover:bg-[#0f2142] transition shadow-md shadow-slate-300 dark:bg-white dark:text-zinc-950 dark:shadow-black/20 dark:hover:bg-zinc-200">
                {pending ? "Sign out and try a different account" : "Retry sign-in"}
              </button>
            </div>
          )}

          {!pending && (
            <p className="mt-6 text-[0.7rem] text-slate-500 text-center dark:text-zinc-500">
              If this keeps happening, take a screenshot of this page and share it
              with your {brandName} administrator.
            </p>
          )}
        </div>
      </div>
    </div>
  )
};
